import { useState } from 'react';
import { personPasst } from '@arbeitsrapporte/shared';

export type Person = { id: string; name: string; email: string };

/** So viele Treffer stehen unter dem Feld; wer mehr braucht, tippt weiter. */
const TREFFERGRENZE = 20;

/**
 * Eine Person – gesucht, dann angetippt. Dieselbe Form wie `Auftragswahl`
 * und `Artikelwahl`: ein Suchfeld, die Treffer darunter, und ist eine
 * gewählt, steht sie allein da, mit „ändern" daneben.
 *
 * Gesucht wird über Name **und** E-Mail, und hinter dem Namen steht immer
 * die Adresse: Zwei „Thomas Müller" unterscheidet nur sie.
 *
 * Gefiltert wird im Browser, über die Liste, die der Aufrufer schon hat:
 * `GET /api/mitarbeiter` blättert nicht – es sind die Personen mit aktiver
 * Rolle, ein paar Dutzend –, und eine Anfrage je Tastendruck brächte keine
 * Zeile, die nicht schon da ist.
 */
export function Mitarbeiterwahl({
  id,
  leute,
  wert,
  onWahl,
  gesperrt = false,
}: {
  /** Kennung des Suchfelds, für das `<label htmlFor>` darüber. */
  id: string;
  leute: Person[];
  /** Die gewählte Person, oder `''`. */
  wert: string;
  onWahl: (id: string) => void;
  gesperrt?: boolean;
}) {
  const [suche, setSuche] = useState('');

  const gewaehlt = wert ? leute.find((m) => m.id === wert) : undefined;

  if (gewaehlt) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded border border-slate-200 bg-slate-50 px-3 py-2">
        <Personenzeile person={gewaehlt} />
        <button
          type="button"
          disabled={gesperrt}
          className="ml-auto min-h-0 px-0 text-sm text-slate-500 underline underline-offset-4 hover:text-slate-900"
          onClick={() => {
            setSuche('');
            onWahl('');
          }}
        >
          ändern
        </button>
      </div>
    );
  }

  const treffer = leute.filter((m) => personPasst(m, suche));
  const gezeigt = treffer.slice(0, TREFFERGRENZE);

  return (
    <div>
      <input
        id={id}
        className="feld"
        placeholder="Name oder E-Mail"
        autoComplete="off"
        value={suche}
        disabled={gesperrt}
        onChange={(e) => setSuche(e.target.value)}
        onKeyDown={(e) => {
          /* Enter wählt den einzigen Treffer – und schickt das Formular nicht ab. */
          if (e.key !== 'Enter') return;
          e.preventDefault();
          const [einziger] = treffer;
          if (einziger && treffer.length === 1) onWahl(einziger.id);
        }}
      />

      {treffer.length === 0 && (
        <p className="mt-2 text-sm text-slate-600">
          {leute.length === 0 ? 'Niemand zu wählen.' : 'Niemand passt zur Suche.'}
        </p>
      )}

      {gezeigt.length > 0 && (
        <ul className="mt-2 divide-y divide-slate-200 rounded border border-slate-200">
          {gezeigt.map((m) => (
            <li key={m.id}>
              <button
                type="button"
                disabled={gesperrt}
                onClick={() => onWahl(m.id)}
                className="block w-full px-3 py-2 text-left font-normal hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Personenzeile person={m} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {treffer.length > gezeigt.length && (
        <p className="mt-1 text-sm text-slate-500">
          {treffer.length - gezeigt.length} weitere – die Suche verfeinern.
        </p>
      )}
    </div>
  );
}

/** Name · E-Mail – so steht eine Person in jeder Auswahl. */
export function Personenzeile({ person }: { person: { name: string; email: string } }) {
  return (
    <span className="min-w-0 break-words">
      <span className="text-slate-900">{person.name}</span>
      {person.email && <span className="text-slate-500"> · {person.email}</span>}
    </span>
  );
}
